import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Download, Loader2 } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { FvGraph, FvOverlayGraph } from "@/components/FvAssessment";

/**
 * Increment C — patient-specific F–V & Performance analytics. Read-only:
 * reuses the existing computed results series and stored fv_profiles. No new
 * calculations, no AI interpretation — rules-based descriptive summaries only.
 */
const primarySide = (item) => {
  const s = item.sides || {};
  const key = s.bilateral ? "bilateral" : (s.right ? "right" : Object.keys(s)[0]);
  return key ? s[key] : null;
};
const delta = (a, b, p = 1) => (a == null || b == null ? null : +(b - a).toFixed(p));
const pctChange = (a, b) => (a == null || b == null || a === 0 ? null : +(((b - a) / Math.abs(a)) * 100).toFixed(1));

function TrendCard({ item }) {
  const s = primarySide(item) || {};
  const hist = (s.history || []).map((h) => ({ ...h }));
  return (
    <div className="fl-card p-4" data-testid={`perf-trend-${item.test_definition_id.slice(0, 8)}`}>
      <div className="flex justify-between items-start mb-2 gap-2">
        <div><div className="fl-heading text-sm">{item.name}</div><div className="text-[11px] text-zinc-400">{item.unit}{item.symmetry ? ` · LSI ${item.symmetry.symmetry_index_pct}%` : ""}</div></div>
        {s.current && <div className="text-xs text-zinc-400 text-right">{s.baseline?.value} → <span className="text-white">{s.current.value}</span>{s.count > 1 && s.pct_change != null ? <span className="text-[#f47a20]"> {s.pct_change > 0 ? "+" : ""}{s.pct_change}%</span> : <span className="text-zinc-600"> · no previous</span>}</div>}
      </div>
      {hist.length > 1 ? (
        <div className="h-40">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={hist} margin={{ top: 6, right: 12, bottom: 4, left: -10 }}>
              <CartesianGrid stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="date" tick={{ fill: "#a1a1aa", fontSize: 10 }} />
              <YAxis tick={{ fill: "#a1a1aa", fontSize: 10 }} />
              <Tooltip contentStyle={{ background: "#18181b", border: "1px solid rgba(255,255,255,0.1)", fontSize: 12 }} />
              <Line dataKey="value" stroke="#f47a20" strokeWidth={2} dot={{ r: 3 }} isAnimationActive={false} name={item.unit} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : <div className="text-[11px] text-zinc-500">Single data point — a trend needs ≥2 compatible assessments.</div>}
    </div>
  );
}

function Section({ title, items, empty }) {
  if (!items.length) return (<div><div className="fl-heading text-base mb-2">{title}</div><div className="text-sm text-zinc-500 mb-6">{empty}</div></div>);
  return (
    <div className="mb-6">
      <div className="fl-heading text-base mb-3">{title}</div>
      <div className="grid md:grid-cols-2 gap-3">{items.map((i) => <TrendCard key={i.test_definition_id} item={i} />)}</div>
    </div>
  );
}

export default function PerformanceDashboard({ patientId, computed = [] }) {
  const [profiles, setProfiles] = useState([]);
  const [aId, setAId] = useState("");
  const [bId, setBId] = useState("");
  const [exporting, setExporting] = useState(false);

  const exportPdf = async () => {
    setExporting(true);
    try {
      const r = await api.get(`/physical-tests/performance-report/patient/${patientId}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `physical-performance-${patientId.slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not generate the performance report");
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    api.get(`/physical-tests/fv-profiles/patient/${patientId}`).then((r) => {
      const rows = r.data || [];
      setProfiles(rows);
      const valid = rows.filter((p) => p.computed?.valid);
      if (valid.length) { setBId(valid[0].id); setAId(valid[1]?.id || valid[0].id); }
    }).catch(() => {});
  }, [patientId]);

  const byCat = (cats) => computed.filter((i) => cats.includes(i.category));
  const jumps = byCat(["jump_power"]);
  const strength = byCat(["dynamic_strength_1rm"]);
  const dynoRom = byCat(["isometric_dynamometry", "rom_mobility"]);

  const validProfiles = profiles.filter((p) => p.computed?.valid);
  const profA = profiles.find((p) => p.id === aId);
  const profB = profiles.find((p) => p.id === bId);
  const latest = validProfiles[0];

  const summaries = useMemo(() => {
    const out = [];
    computed.forEach((i) => {
      const s = primarySide(i);
      if (s?.baseline && s?.current && s.baseline.date !== s.current.date && s.pct_change != null) {
        const dir = s.pct_change >= 0 ? "increased" : "decreased";
        out.push(`${i.name} ${dir} from ${s.baseline.value} to ${s.current.value} ${i.unit} (${s.pct_change > 0 ? "+" : ""}${s.pct_change}%) between ${s.baseline.date} and ${s.current.date}.`);
      }
    });
    validProfiles.slice(0, 1).forEach((p) => {
      out.push(`Latest force–velocity profile (${(p.performed_at || "").slice(0, 10)}, ${p.protocol}): F0 ${p.computed.f0_n} N, V0 ${p.computed.v0_ms} m/s, Pmax ${p.computed.pmax_w} W (R² ${p.computed.r2}).`);
    });
    return out;
  }, [computed, validProfiles]);

  const cmp = (key, unit, prec = 1) => {
    if (!profA?.computed?.valid || !profB?.computed?.valid) return null;
    const a = profA.computed[key], b = profB.computed[key];
    return <span>{a} → <span className="text-white">{b}</span> {unit} <span className="text-[#f47a20]">({pctChange(a, b) != null ? `${pctChange(a, b) > 0 ? "+" : ""}${pctChange(a, b)}%` : "—"}, Δ {delta(a, b, prec)})</span></span>;
  };

  return (
    <div data-testid="performance-dashboard">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="fl-heading text-xl">F–V &amp; Performance</div>
        <button type="button" onClick={exportPdf} disabled={exporting}
          className="fl-btn text-sm inline-flex items-center gap-1 disabled:opacity-60"
          data-testid="perf-export-pdf">
          {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
          Export performance PDF
        </button>
      </div>
      {/* Panel A — Force–Velocity */}
      <div className="mb-6">
        <div className="fl-heading text-base mb-2">Force–Velocity profile</div>
        {!latest ? (
          <div className="text-sm text-zinc-500" data-testid="perf-fv-empty">No valid force–velocity profile yet. Record a loaded squat-jump assessment (≥3 distinct loads) under Record Result → Force–Velocity Assessment.</div>
        ) : (
          <>
            <div className="fl-card p-4" data-testid="perf-fv-latest">
              <div className="text-xs text-zinc-400 mb-2">Latest · {(latest.performed_at || "").slice(0, 10)} · {latest.protocol} · Samozino SJ 2008 ({latest.computed.method_version})</div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-2 text-center">
                {[["F0", latest.computed.f0_n, "N"], ["V0", latest.computed.v0_ms, "m/s"], ["Pmax", latest.computed.pmax_w, "W"], ["Slope", latest.computed.sfv_n_per_ms, "N/(m/s)"], ["R²", latest.computed.r2, ""]].map(([l, v, u]) => (
                  <div key={l}><div className="fl-label">{l}</div><div className="fl-heading text-lg text-[#f47a20]">{v}<span className="text-xs text-zinc-400"> {u}</span></div></div>
                ))}
              </div>
              <FvGraph computed={latest.computed} />
              <div className="text-[11px] text-zinc-500 mt-1">Orange line = theoretical model (extrapolated to F0/V0 intercepts). Blue points = measured loading conditions. {(latest.computed.warnings || []).join(" ")}</div>
            </div>

            {validProfiles.length > 1 && (
              <div className="fl-card p-4 mt-3" data-testid="perf-fv-compare">
                <div className="fl-label mb-2">Compare two assessments (same protocol only)</div>
                <div className="flex flex-wrap gap-2 mb-3">
                  <select className="fl-input text-sm px-2 py-1 rounded-md" value={aId} onChange={(e) => setAId(e.target.value)} data-testid="perf-fv-a">
                    {validProfiles.map((p) => <option key={p.id} value={p.id}>Baseline: {(p.performed_at || "").slice(0, 10)}</option>)}
                  </select>
                  <select className="fl-input text-sm px-2 py-1 rounded-md" value={bId} onChange={(e) => setBId(e.target.value)} data-testid="perf-fv-b">
                    {validProfiles.map((p) => <option key={p.id} value={p.id}>Current: {(p.performed_at || "").slice(0, 10)}</option>)}
                  </select>
                </div>
                {profA?.protocol !== profB?.protocol && <div className="text-[11px] text-amber-400 mb-2">⚠ Protocols differ — comparison may not be valid.</div>}
                <div className="grid md:grid-cols-3 gap-2 text-sm mb-3">
                  <div>F0: {cmp("f0_n", "N")}</div><div>V0: {cmp("v0_ms", "m/s", 3)}</div><div>Pmax: {cmp("pmax_w", "W")}</div>
                </div>
                {profA?.computed?.valid && profB?.computed?.valid && (
                  <div data-testid="perf-fv-overlay">
                    <div className="fl-label mb-1">Overlaid force–velocity curves</div>
                    <FvOverlayGraph series={[
                      { computed: profA.computed, label: `Baseline · ${(profA.performed_at || "").slice(0, 10)}`, color: "#38bdf8" },
                      { computed: profB.computed, label: `Current · ${(profB.performed_at || "").slice(0, 10)}`, color: "#f47a20" },
                    ]} />
                    <div className="text-[11px] text-zinc-500 mt-1">Both theoretical F–v lines + their measured condition points, extrapolated to each profile's own F0/V0 intercepts. Overlay is descriptive — no combined model is fitted.</div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      <Section title="Jump performance" items={jumps} empty="No jump measurements recorded yet." />
      <Section title="Maximal strength (1RM)" items={strength} empty="No 1RM assessments recorded yet." />
      <Section title="Isometric dynamometry & ROM" items={dynoRom} empty="No dynamometer or ROM measurements recorded yet." />

      <div className="fl-card p-4" data-testid="perf-summaries">
        <div className="fl-heading text-base mb-2">Descriptive summary</div>
        {summaries.length ? (
          <ul className="list-disc ml-5 space-y-1 text-sm text-zinc-300">{summaries.map((s, i) => <li key={i} data-testid={`perf-summary-${i}`}>{s}</li>)}</ul>
        ) : <div className="text-sm text-zinc-500" data-testid="perf-summary-empty">Insufficient comparable data to summarise changes. Record at least two compatible assessments.</div>}
        <div className="text-[11px] text-zinc-500 mt-3">Rules-based numerical summaries only — not clinical interpretation, diagnosis or return-to-sport clearance. Changes are not labelled clinically meaningful (no validated measurement-error thresholds applied).</div>
      </div>
    </div>
  );
}
